import { AlertCircle, CheckCircle2, Copy, Ticket } from "lucide-react";
import { useState } from "react";

import { SectionLabel } from "components/AppShell";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Skeleton } from "components/ui/skeleton";
import { Badge } from "components/ui/badge";

import { AdminCommandFeedback } from "../AdminCommandFeedback";
import { useAdminCommand, type AdminCommandState } from "../use-admin-command";
import { useEventInvites } from "../use-event-invites";
import { APP_LOCALE } from "utils";

/**
 * Platform invites, from this event (D21).
 *
 * The token is generated by the backend, and its one reveal is the mint
 * success panel below — the mirror never carries it, so losing the panel
 * means minting again rather than going looking. What the list shows is the
 * honest state an admin needs: how much of each invite was used, when it
 * dies, whether it was pulled, and who minted it (a colleague's cannot be
 * revoked by you — that is the settled model, and the button says so by
 * being disabled).
 */
export function InvitesSection({
  eventId,
  uid,
  eventSlug,
}: {
  eventId: string;
  uid: string;
  /** Set only for a published event: the page the invitee is sent to. */
  eventSlug?: string;
}) {
  // Live: a mint or revoke shows up through the listener, with no reload.
  const { state: invites } = useEventInvites(eventId);
  const mint = useAdminCommand();
  const revoke = useAdminCommand();
  const [origin] = useState(() => window.location.origin);

  return (
    <div className="space-y-6" data-testid="invites-content">
      <MintCard
        eventId={eventId}
        uid={uid}
        mint={mint}
        origin={origin}
        eventSlug={eventSlug}
      />
      <Card className="border-border shadow-none">
        <CardContent className="space-y-4 pt-6">
          <SectionLabel>Existing invites</SectionLabel>
          <InvitesList
            state={invites}
            uid={uid}
            busy={
              revoke.state.status === "submitting" ||
              revoke.state.status === "watching"
            }
            onRevoke={(inviteId) =>
              revoke.submit("REVOKE_INVITE", eventId, uid, { inviteId })
            }
          />
          <AdminCommandFeedback
            state={revoke.state}
            onReset={revoke.reset}
            testId="invite-revoke"
          />
        </CardContent>
      </Card>
    </div>
  );
}

function MintCard({
  eventId,
  uid,
  mint,
  origin,
  eventSlug,
}: {
  eventId: string;
  uid: string;
  mint: ReturnType<typeof useAdminCommand>;
  origin: string;
  eventSlug?: string;
}) {
  const [label, setLabel] = useState("");
  const [expiryDays, setExpiryDays] = useState("14");
  const [maxUses, setMaxUses] = useState("20");

  const busy =
    mint.state.status === "submitting" || mint.state.status === "watching";
  const days = Number(expiryDays);
  const uses = Number(maxUses);
  const canSubmit =
    !busy &&
    label.trim().length <= 200 &&
    Number.isInteger(days) &&
    days >= 1 &&
    days <= 365 &&
    Number.isInteger(uses) &&
    uses >= 1 &&
    uses <= 500;

  const token =
    mint.state.status === "succeeded" ? mint.state.invite?.token : undefined;

  return (
    <Card className="border-border shadow-none">
      <CardContent className="space-y-4 pt-6">
        <SectionLabel>Invite people</SectionLabel>
        <p className="text-sm text-muted-foreground">
          An invite seat is a sign-in, not a registration: holders get a
          passwordless way in, once each, until the count runs out or the invite
          expires. Anyone outside the platform&apos;s regular domains needs one
          on their first sign-in only.
        </p>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-1.5 sm:col-span-1">
            <Label htmlFor="invite-label">Label</Label>
            <Input
              id="invite-label"
              testId="invite-label"
              value={label}
              placeholder="Press desk"
              maxLength={200}
              disabled={busy}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="invite-expiry">Valid for (days)</Label>
            <Input
              id="invite-expiry"
              testId="invite-expiry"
              type="number"
              min={1}
              max={365}
              value={expiryDays}
              disabled={busy}
              onChange={(e) => setExpiryDays(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="invite-max-uses">Max uses</Label>
            <Input
              id="invite-max-uses"
              testId="invite-max-uses"
              type="number"
              min={1}
              max={500}
              value={maxUses}
              disabled={busy}
              onChange={(e) => setMaxUses(e.target.value)}
            />
          </div>
        </div>
        <Button
          variant="cta"
          onClick={() => {
            if (!canSubmit) return;
            void mint.submit("MINT_INVITE", eventId, uid, {
              label: label.trim(),
              expiresInDays: days,
              maxUses: uses,
            });
          }}
          disabled={!canSubmit}
          data-testid="mint-invite"
        >
          <Ticket className="h-4 w-4" />
          Mint invite
        </Button>
        <ShowMintState
          mint={mint.state}
          link={token ? inviteLink(origin, token, eventSlug) : undefined}
          onReset={mint.reset}
        />
      </CardContent>
    </Card>
  );
}

/**
 * The token reveal, and why the label is "copy it now": the token cannot be
 * read back from Firestore afterwards (the invite document only stores the
 * hash), so a closed panel means minting again.
 */
function ShowMintState({
  mint,
  link,
  onReset,
}: {
  mint: AdminCommandState;
  link: string | undefined;
  onReset: () => void;
}) {
  if (link) {
    return (
      <div
        className="space-y-2 rounded-md border border-status-success bg-status-success/15 p-3 text-foreground"
        data-testid="invite-minted"
      >
        <p className="flex items-center gap-2 text-sm font-medium text-foreground">
          <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
          Invite minted. Share the sign-in link:
        </p>
        <div className="flex gap-2">
          <Input
            readOnly
            value={link}
            className="font-mono sm:text-xs"
            testId="invite-share-link"
            onFocus={(e) => e.currentTarget.select()}
          />
          <Button
            variant="outline"
            size="sm"
            data-testid="invite-share-link-copy"
            onClick={() => {
              void navigator.clipboard.writeText(link);
              onReset();
            }}
          >
            <Copy className="h-4 w-4" />
            Copy
          </Button>
        </div>
      </div>
    );
  }
  return (
    <AdminCommandFeedback state={mint} onReset={onReset} testId="invite-mint" />
  );
}

function InvitesList({
  state,
  uid,
  busy,
  onRevoke,
}: {
  state: ReturnType<typeof useEventInvites>["state"];
  uid: string;
  busy: boolean;
  onRevoke: (inviteId: string) => void;
}) {
  if (state.status === "loading") {
    return (
      <div className="grid grid-cols-4 gap-3" aria-hidden>
        <Skeleton className="h-8" />
        <Skeleton className="h-8" />
        <Skeleton className="h-8" />
        <Skeleton className="h-8" />
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <p
        className="flex items-center gap-2 text-sm text-destructive-text"
        data-testid="invites-error"
      >
        <AlertCircle className="h-4 w-4 shrink-0" />
        {state.error}
      </p>
    );
  }
  if (state.invites.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No invites yet for this event.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {state.invites.map((invite) => {
        const revoked = Boolean(invite.revokedAt);
        const full = invite.issued >= invite.maxUses;
        return (
          <div
            key={invite.id}
            className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border px-3 py-2"
            data-testid="invite-row"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-foreground">
                {invite.label ?? "Invite"}
                {revoked && " — revoked"}
              </p>
              <p className="text-xs text-muted-foreground">
                {invite.issued}/{invite.maxUses} seats issued, {invite.uses}{" "}
                signed in
                {invite.expiresAt &&
                  ` · until ${formatDate(invite.expiresAt.toDate())}`}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {fullOrRevokedBadge(revoked, full)}
              <Button
                variant="ghost"
                size="sm"
                disabled={busy || revoked || full || invite.createdBy !== uid}
                aria-label={
                  invite.createdBy !== uid
                    ? "You may only revoke invites you minted"
                    : `Revoke invite`
                }
                title={
                  invite.createdBy !== uid
                    ? "You may only revoke invites you minted."
                    : undefined
                }
                data-testid={`revoke-invite-${invite.id}`}
                onClick={() => onRevoke(invite.id)}
              >
                Revoke
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function fullOrRevokedBadge(revoked: boolean, full: boolean) {
  if (revoked)
    return (
      <Badge variant="outline" data-testid="invite-revoked">
        revoked
      </Badge>
    );
  if (full)
    return (
      <Badge variant="outline" data-testid="invite-full">
        full
      </Badge>
    );
  return null;
}

function formatDate(timestamp: Date): string {
  return timestamp.toLocaleDateString(APP_LOCALE, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * The sign-in link an invite is shared as. An invite minted for a published
 * event carries that event page as the redirect, so the invitee lands on the
 * event after signing in. The sign-in route keeps a redirect only when it is
 * a path on this site.
 */
export function inviteLink(
  origin: string,
  token: string,
  eventSlug?: string,
): string {
  const params = new URLSearchParams({ invite: token });
  if (eventSlug) params.set("redirect", `/e/${eventSlug}`);
  return `${origin}/sign-in?${params.toString()}`;
}
